import { CACHE_CONFIG_TYPES } from '@intigral/constant';
import { WATERMARK } from '../config/constants';
import user from './user';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { PlayerService } from './bitmovinPlayer';
import { PlayerAPI } from 'bitmovin-player';

/**
 * Watermarking service for player
 * @purpose Appends watermark overlay to player and temper protection
 */
class WaterMark {
    private config: any;
    private reloadTimeoutId: ReturnType<typeof setTimeout> | null;
    private verificationImgTag: HTMLImageElement;
    private player: PlayerAPI;
    private watermarkOverlayId: string;
    private watermarkOverlay: HTMLImageElement | null;
    private checkWatermarkOverlayStylingIntervalId: ReturnType<typeof setInterval> | null;
    private watermarkOverlayMutationObserver: MutationObserver | null;
    private containerMutationObserver: MutationObserver | null;
    private videoElement: HTMLVideoElement | null;
    private terminated: boolean;
    private allowedWatermarkOverlayStyleAttributes: any;
    private requiredMatchingStyleAttributes: string[];
    private token: string;
    private requestUrl: string;
    private isWatermarkEnabled: boolean;
    private request: XMLHttpRequest | null;
    private watermarkOverlaySourceChangePending: boolean;

    constructor(player: PlayerAPI) {
        this.config = this.getWatermarkConfig();
        this.reloadTimeoutId = null;
        this.verificationImgTag = document.createElement('img');
        this.player = player;
        this.watermarkOverlayId = 'watermark-overlay';
        this.watermarkOverlay = null;
        this.checkWatermarkOverlayStylingIntervalId = null;
        this.watermarkOverlayMutationObserver = null;
        this.containerMutationObserver = null;
        this.videoElement = null;
        this.terminated = false;
        this.allowedWatermarkOverlayStyleAttributes = {
            display: ['block', 'inline-block'],
            visibility: ['visible'],
            position: ['absolute'],
        };
        this.requiredMatchingStyleAttributes = ['width', 'height', 'top', 'left', 'margin', 'padding', 'zIndex'];

        const { wmToken = WATERMARK.USER_TOKEN } = user.getUserInfo() || {};
        this.token = wmToken;

        /* CMS configurations */
        const { isEnabled = false, apiEndpoint = '' } = this.config || {};

        this.requestUrl = apiEndpoint;
        this.isWatermarkEnabled = apiEndpoint !== '' ? isEnabled : false;
    }

    /**
     * Get watermark CMS config
     * @returns object | boolean
     */
    getWatermarkConfig(configParam?: any): any {
        try {
            // Use provided config or fallback to localStorage for backward compatibility
            const newConfig = configParam;
            return newConfig?.player?.watermark;
        } catch (e) {
            return;
        }
    }

    /**
     * Convert array buffer to base64 string
     * @param arrayBuffer - The array buffer
     * @returns string
     */
    arrayBufferToBase64String(arrayBuffer: ArrayBuffer): string {
        return btoa(
            new Uint8Array(arrayBuffer).reduce(function (data, byte) {
                return data + String.fromCharCode(byte);
            }, '')
        );
    }

    /**
     * Stop watermarking
     */
    stop(): void {
        clearTimeout(this.reloadTimeoutId!);
        if (this.request) {
            this.request.removeEventListener('readystatechange', this.onReadystateChange);
            this.request.removeEventListener('error', this.onRequestError);
            this.request.abort();
        }
    }

    /**
     * On image verification error handler
     */
    onImageVerificationError(): void {
        if (typeof this.stop === 'function') {
            this.stop();
        }

        if (typeof this.onWatermarkError === 'function') {
            this.onWatermarkError('verification-error');
        }
    }

    /**
     * Schedule reload
     * This method execute after API request is finished - it stores expiry time of image
     * and set timeout of next request
     */
    scheduleReload(): void {
        const cacheControlResponseHeader = this.request!.getResponseHeader('Cache-Control');
        const maxAgeMatches = /^.*max-age=(\d+).*$/.exec(cacheControlResponseHeader!);
        const maxAge = maxAgeMatches && maxAgeMatches.length >= 2 ? parseFloat(maxAgeMatches[1]) : Infinity;
        if (isFinite(maxAge)) {
            this.reloadTimeoutId = setTimeout(this.load.bind(this), (maxAge / 2) * 1000);
        }
    }

    /**
     * On success request handler
     */
    onRequestSuccess(): void {
        const imageDataUri = 'data:image/png;base64,' + this.arrayBufferToBase64String(this.request!.response);

        this.verificationImgTag.src = imageDataUri;
        this.successCallback(imageDataUri);
        this.scheduleReload();
    }

    /**
     * On state change handler
     */
    onReadystateChange(): void {
        if (this.request!.readyState === XMLHttpRequest.DONE && this.request!.status === 200) {
            this.onRequestSuccess();
            this.request = null;
        } else if (this.request!.readyState === XMLHttpRequest.DONE) {
            this.onRequestError();
            this.request = null;
        }
    }

    /**
     * On error handler
     */
    onRequestError(): void {
        if (typeof this.stop === 'function') {
            this.stop();
        }

        if (typeof this.onWatermarkError === 'function') {
            this.onWatermarkError('loading-error');
        }
    }

    /**
     * Loads watermark by requesting the image with user token
     */
    load(): void {
        if (this.player?.getVideoElement() == null) {
            return;
        }

        clearTimeout(this.reloadTimeoutId!);

        this.request = new XMLHttpRequest();
        this.request.open('GET', this.requestUrl);
        this.request.responseType = 'arraybuffer';
        this.request.setRequestHeader('Authorization', 'Bearer ' + this.token);
        this.request.addEventListener('readystatechange', this.onReadystateChange.bind(this));
        this.request.addEventListener('error', this.onRequestError);

        this.request.send();
    }

    /**
     * Create watermark overlay
     * @returns object
     */
    createWatermarkOverlay(): HTMLImageElement {
        this.watermarkOverlay = document.createElement('img');

        this.setupWatermarkOverlay();
        this.setupWatermarkOverlayTamperProtection();

        return this.watermarkOverlay;
    }

    /**
     * On error handler for watermark overlay
     */
    onImgError(): void {
        this.terminatePlayback();
    }

    /**
     * Setup watermark properties of overlay
     */
    setupWatermarkOverlay(): void {
        this.watermarkOverlay!.id = this.watermarkOverlayId;
        this.watermarkOverlay!.style.position = 'absolute';
        this.watermarkOverlay!.style.left = '0';
        this.watermarkOverlay!.style.top = '0';
        this.watermarkOverlay!.style.width = '100%';
        this.watermarkOverlay!.style.height = '100%';

        this.videoElement!.parentElement!.insertBefore(this.watermarkOverlay!, this.videoElement!.nextSibling);
    }

    /**
     * On change observer for overlay
     */
    onWatermarkOverlaySourceChanged(): void {
        if (this.watermarkOverlaySourceChangePending) {
            this.watermarkOverlaySourceChangePending = false;
        } else {
            this.terminatePlayback();
        }
    }

    /**
     * Check overlay styling and compare with the default properties
     * if style is different then terminate the player
     */
    checkWatermarkOverlayStyling(): void {
        const computedVideoElementStyles = getComputedStyle(this.videoElement!);
        const computedWatermarkOverlayStyles = getComputedStyle(this.watermarkOverlay!);

        for (const key in this.allowedWatermarkOverlayStyleAttributes) {
            if (Object.prototype.hasOwnProperty.call(this.allowedWatermarkOverlayStyleAttributes, key)) {
                if (
                    this.allowedWatermarkOverlayStyleAttributes[key].indexOf(computedWatermarkOverlayStyles[key]) === -1
                ) {
                    this.terminatePlayback();
                }
            }
        }

        for (let i = 0; i < this.requiredMatchingStyleAttributes.length; i++) {
            const key = this.requiredMatchingStyleAttributes[i];

            if (computedWatermarkOverlayStyles[key] !== computedVideoElementStyles[key]) {
                this.terminatePlayback();
            }
        }
    }

    /**
     * Observer for style changes or parent tag changes
     * Decides which attribute is changed and call the validation methods
     * @param mutationRecord - The mutation record
     */
    onWatermarkOverlayAttributeMutation(mutationRecord: MutationRecord): void {
        if (mutationRecord.attributeName === 'src') {
            this.onWatermarkOverlaySourceChanged();
        } else if (mutationRecord.attributeName === 'style') {
            this.checkWatermarkOverlayStyling();
        }
    }

    /**
     * Check if child elements from container is removed or not
     * If removed - Terminate the playback video player
     * @param mutationRecord - The mutation record
     */
    onContainerChildElementRemoved(mutationRecord: MutationRecord): void {
        for (let i = 0; i < mutationRecord.removedNodes.length; i++) {
            if (mutationRecord.removedNodes[i].isEqualNode(this.watermarkOverlay)) {
                this.terminatePlayback();
            }
        }
    }

    /**
     * Check order of container's child elements
     * Terminate the player if illegal watermark is placed
     */
    checkContainerChildOrdering(): void {
        let videoElementIndex = -1;
        const children = this.watermarkOverlay?.parentElement?.children;

        for (let i = 0; i < children!.length; i++) {
            if (children![i] === this.videoElement) {
                videoElementIndex = i;
            } else if (children![i] === this.watermarkOverlay && (videoElementIndex !== i - 1 || i === 0)) {
                this.terminatePlayback();
            }
        }
    }

    /**
     * Analyze container mutation record
     * @param mutationRecord - The mutation record
     */
    analyzeContainerMutationRecord(mutationRecord: MutationRecord): void {
        if (
            mutationRecord.type === 'childList' &&
            mutationRecord.removedNodes &&
            mutationRecord.removedNodes.length > 0
        ) {
            this.onContainerChildElementRemoved(mutationRecord);
        } else if (mutationRecord.type === 'childList') {
            this.checkContainerChildOrdering();
        }
    }

    /**
     * Analyze watermark overlay mutation record
     * @param mutationRecord - The mutation record
     */
    analyzeWatermarkOverlayMutationRecord(mutationRecord: MutationRecord): void {
        if (mutationRecord.type === 'attributes') {
            this.onWatermarkOverlayAttributeMutation(mutationRecord);
        }
    }

    /**
     * On watermark overlay mutation
     * @param mutationRecords - The mutation records
     */
    onWatermarkOverlayMutation(mutationRecords: MutationRecord[]): void {
        for (let i = 0; i < mutationRecords.length; i++) {
            this.analyzeWatermarkOverlayMutationRecord(mutationRecords[i]);
        }
    }

    /**
     * Setup watermark overlay tamper protection
     * It adds two mutations of overlay
     *  1. Watermark style change observer
     *  2. Video container observer
     */
    setupWatermarkOverlayTamperProtection(): void {
        this.watermarkOverlayMutationObserver = new MutationObserver(this.onWatermarkOverlayMutation.bind(this));
        this.watermarkOverlayMutationObserver.observe(this.watermarkOverlay, {
            childList: false,
            attributes: true,
            characterData: true,
            subtree: false,
            attributeOldValue: true,
            characterDataOldValue: true,
        });

        this.containerMutationObserver = new MutationObserver(this.onContainerMutation.bind(this));
        this.containerMutationObserver.observe(this.watermarkOverlay?.parentElement, {
            childList: true,
            attributes: false,
            characterData: false,
        });

        this.checkWatermarkOverlayStylingIntervalId = setInterval(this.checkWatermarkOverlayStyling.bind(this), 1000);
    }

    /**
     * Callback of video container observer
     * @param mutationRecords - The mutation records
     */
    onContainerMutation(mutationRecords: MutationRecord[]): void {
        for (let i = 0; i < mutationRecords.length; i++) {
            this.analyzeContainerMutationRecord(mutationRecords[i]);
        }
    }

    /**
     * Returns the overlay
     * If overlay does not exist then create new one
     * @returns object
     */
    getWatermarkOverlay(): HTMLImageElement {
        return this.watermarkOverlay || this.createWatermarkOverlay();
    }

    /**
     * Update watermark remote URL into watermark overlay
     * @param watermarkDataUri - The watermark data URI
     */
    updateWatermarkOverlay(watermarkDataUri: string): void {
        this.watermarkOverlay = this.getWatermarkOverlay();
        this.watermarkOverlaySourceChangePending = true;
        this.watermarkOverlay.src = watermarkDataUri;
    }

    /**
     * Success callback of API call to get overlay image
     * @param watermarkDataUri - The watermark data URI
     */
    successCallback(watermarkDataUri: string): void {
        this.videoElement = this.player.getVideoElement();
        this.updateWatermarkOverlay(watermarkDataUri);
    }

    /**
     * Remove watermark overlay
     */
    removeWatermarkOverlay(): void {
        const watermarkOverlay = document.querySelector('#' + this.watermarkOverlayId);

        clearInterval(this.checkWatermarkOverlayStylingIntervalId!);

        if (!watermarkOverlay) {
            return;
        }

        this.watermarkOverlayMutationObserver!.disconnect();
        this.containerMutationObserver!.disconnect();
        this.watermarkOverlay!.removeEventListener('error', this.onImgError.bind(this));
        this.watermarkOverlay!.parentElement!.removeChild(watermarkOverlay);
        this.watermarkOverlay = null;
    }

    /**
     * Terminates the player and remove the watermark
     */
    terminatePlayback(): void {
        if (this.terminated) {
            return;
        }

        if (typeof this.removeWatermarkOverlay === 'function') {
            this.removeWatermarkOverlay();
        }

        if (typeof this.stop === 'function') {
            this.stop();
        }

        this.terminated = true;
    }

    /**
     * On error watermark callback
     * @param error - The error
     */
    onWatermarkError(error: string): void {
        this.terminatePlayback();
    }

    /**
     * Watermark service setup
     */
    setup(): void {
        if (this.isWatermarkEnabled) {
            this.verificationImgTag.addEventListener('error', this.onImageVerificationError.bind(this));
            this.load();
        }
    }
}

export default WaterMark;
